import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { User, LogOut, Settings, Sliders, Volume2, Sparkles, Target, Shield, School, Edit2, Save, X, Link as LinkIcon, Copy, CheckCircle2, Eye, Heart, GraduationCap, Camera, Trash2, Calendar, AlertCircle, Users } from 'lucide-react';
import { Toggle } from '../ui/Toggle';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { AppState } from '../../app/types';
import { GroupId, StartingPeriod } from '../../config/systemConfig';
import { type AccountsState, isLearnerAccount, isTeacherAccount } from '../../accounts';
import { getAccountRoleLabel } from '../../accounts/logic/uiHelpers';
import { deleteAssessmentDataForLearner } from '../../assessment/utils/assessmentStorage';
import { isRosterEnabled, setRosterEnabled, forgetProfile } from '../../data/supabase/deviceRoster';
import { PageContainer } from '../shared/PageContainer';
import { PageHero } from '../shared/PageHero';
import { WorldZone } from '../shared/WorldZone';
import { itemVariants } from '../shared/animations';
import { useDjaySelection } from '../../mascot';
import { dataLayer, type LearnerPreferences } from '../../data/dataLayer';
import { useAppStore } from '../../app/hooks/useAppStore';
import { getProgressToNextLevel } from '../../config/constants';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { ProgressBar } from '../ui/Progress';
import { soundManager } from '../../utils/soundManager';
import { playSoundIntent, type SoundIntent } from '../../utils/sound/soundIntent';
import { getAutoPhaseLabel } from '../../mastery';
import { SUPABASE_AUTH_ENABLED, getSessionState, subscribeToSession, shouldUseBackend, fetchLinkedPeople, type SessionState } from '../../data/supabase/session';
const DEFAULT_PREFS: LearnerPreferences = {
  sound: true,
  reducedMotion: false,
  dailyGoal: 10
};
// App-Feel 2027 W2 — sound-mix sliders. Each control maps a persisted 0..1
// volume field to a category, plus a preview intent played when the value is
// committed so the child immediately hears the effect of the slider.
const SOUND_MIX_SLIDERS: {
  key: 'masterVolume' | 'voiceVolume' | 'celebrationVolume' | 'uiVolume';
  label: string;
  hint: string;
  accent: string;
  preview: SoundIntent;
}[] = [{
  key: 'masterVolume',
  label: 'Hoofdvolume',
  hint: 'Alle geluiden samen',
  accent: 'orange',
  preview: 'reward.streak'
}, {
  key: 'voiceVolume',
  label: "Djay's stem",
  hint: 'Het blup-geluidje van Djay',
  accent: 'sky',
  preview: 'mascot.speak'
}, {
  key: 'celebrationVolume',
  label: 'Vieringen',
  hint: 'Goede antwoorden en beloningen',
  accent: 'amber',
  preview: 'reward.levelUp'
}, {
  key: 'uiVolume',
  label: 'Klikgeluiden',
  hint: 'Tikken, klikken en navigeren',
  accent: 'emerald',
  preview: 'nav.click'
}];
const ACCENT_RANGE: Record<string, string> = {
  orange: 'accent-orange-500',
  sky: 'accent-sky-500',
  amber: 'accent-amber-500',
  emerald: 'accent-emerald-500'
};
const ACCENT_TEXT: Record<string, string> = {
  orange: 'text-orange-600',
  sky: 'text-sky-600',
  amber: 'text-amber-600',
  emerald: 'text-emerald-600'
};
interface SettingsPageProps {
  state: AppState;
  selectedGroup: GroupId;
  startingPeriod: StartingPeriod;
  manualPeriod?: StartingPeriod | null;
  onSetStartingPeriod: (period: StartingPeriod | null) => void;
  onUpdateName?: (newName: string) => void;
  accounts?: AccountsState;
  onLogout?: () => void;
  viewingLearnerId?: string | null;
}
export function SettingsPage({
  state,
  selectedGroup,
  startingPeriod,
  manualPeriod,
  onSetStartingPeriod,
  onUpdateName,
  accounts,
  onLogout,
  viewingLearnerId
}: SettingsPageProps) {
  const {
    resetLearnerProgress,
    joinClassroomByCode,
    deleteAccount
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  // Persisted preferences — loaded async from dataLayer
  const [prefs, setPrefs] = useState<LearnerPreferences>(DEFAULT_PREFS);
  // Load preferences from dataLayer on mount
  useEffect(() => {
    dataLayer.getLearnerPreferences().then(setPrefs).catch(() => {});
  }, []);
  // Save preferences to dataLayer on change
  useEffect(() => {
    dataLayer.saveLearnerPreferences(prefs);
  }, [prefs]);
  // ── Opslagstatus ───────────────────────────────────────────────────
  // Tijdens de migratie naar Supabase is niet zichtbaar of een account écht
  // op de server staat of alleen lokaal. Dat verschil is juridisch bepalend
  // (verwerken wij kinderdata, of blijft alles op het apparaat?), dus het hoort
  // afleesbaar te zijn in plaats van af te leiden uit gedrag.
  const [session, setSession] = useState<SessionState>(() => SUPABASE_AUTH_ENABLED ? getSessionState() : {
    status: 'anonymous'
  } as SessionState);
  useEffect(() => {
    if (!SUPABASE_AUTH_ENABLED) return;
    return subscribeToSession(setSession);
  }, []);

  // Wie kijkt er mee — rechtstreeks van de server, niet uit lokale staat.
  // Een kind moet dit kunnen zien op élk apparaat, ook waar de ouder nooit
  // heeft ingelogd.
  const [serverParents, setServerParents] = useState<{
    id: string;
    name: string;
  }[] | null>(null);
  useEffect(() => {
    if (!SUPABASE_AUTH_ENABLED) return;
    if (session.status !== 'authenticated') return;
    if (session.profile.role !== 'learner') return;
    void fetchLinkedPeople().then(people => setServerParents(people.filter(p => p.relation === 'parent' || p.relation === 'teacher').map(p => ({
      id: p.id,
      name: p.name
    }))));
  }, [session]);

  // ── Afmelden op dit apparaat ───────────────────────────────────────
  // Gewoon uitloggen laat de leerdata op het apparaat staan. Dat is bewust —
  // wissen zou voor een solo-account dataverlies zijn, geen opschoning. Maar op
  // een geleend of gedeeld apparaat wil je juist wél schoon achterlaten. Dat
  // zijn twee verschillende wensen, dus twee knoppen.
  const [rosterOn, setRosterOn] = useState(() => isRosterEnabled());
  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false);
  const [wiping, setWiping] = useState(false);
  const syncsToServer = session.status === 'authenticated' && (session.profile.dataController === 'school' || session.profile.consentStatus === 'granted');
  const hasServerAccount = session.status === 'authenticated';
  const confirmWipeDevice = async () => {
    const learnerId = accounts?.activeAccountId;
    if (!learnerId) return;
    setWiping(true);
    try {
      // Eerst de toetsgegevens (aparte opslag), dan het account met zijn
      // voortgang. In deze volgorde, want na het verwijderen van het account
      // is niet meer te bepalen wélke toetspogingen van dit kind waren.
      await deleteAssessmentDataForLearner(learnerId);
      deleteAccount(learnerId);
      // Ook de tegel weghalen. Blijft die staan, dan wijst hij naar gegevens
      // die er niet meer zijn — en suggereert hij dat er nog iets van dit kind
      // op dit apparaat staat.
      forgetProfile(learnerId);
    } finally {
      setWiping(false);
      setWipeConfirmOpen(false);
      onLogout?.();
    }
  };
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const confirmResetProgress = () => {
    const learnerId = viewingLearnerId || accounts?.activeAccountId;
    if (learnerId) {
      resetLearnerProgress(learnerId);
      onSetStartingPeriod(null);
    }
    setResetConfirmOpen(false);
  };
  const updatePref = <K extends keyof LearnerPreferences,>(key: K, value: LearnerPreferences[K]) => {
    setPrefs(prev => {
      const newPrefs = {
        ...prev,
        [key]: value
      };
      if (key === 'sound' || key === 'masterVolume' || key === 'voiceVolume' || key === 'celebrationVolume' || key === 'uiVolume') {
        // Refresh sound manager's cached on/off + volume mix immediately.
        setTimeout(() => soundManager.refreshPreference(), 0);
      }
      return newPrefs;
    });
  };
  // W2 — play a short preview of a category when its slider is committed, so
  // the child hears the effect of the change. Refresh the cache first (the
  // save effect has flushed localStorage by the time this fires), then play.
  const previewSound = (intent: SoundIntent) => {
    setTimeout(() => {
      soundManager.refreshPreference();
      setTimeout(() => playSoundIntent(intent), 70);
    }, 0);
  };
  const activeAccount = accounts ? accounts.accountsById[accounts.activeAccountId] : null;
  const isViewingLearner = !!viewingLearnerId && (activeAccount?.role === 'parent' || activeAccount?.role === 'teacher');
  const displayName = isViewingLearner ? state.stats.name : activeAccount?.displayName || state.stats.name;
  const accountRole = activeAccount?.role || 'learner';
  const roleLabel = getAccountRoleLabel(accountRole);
  const isLearner = accountRole === 'learner';
  // Name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(displayName);
  useEffect(() => {
    setEditedName(displayName);
  }, [displayName]);
  const handleSaveName = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== displayName && onUpdateName) {
      onUpdateName(trimmedName);
      setIsEditingName(false);
    } else {
      setEditedName(displayName);
      setIsEditingName(false);
    }
  };
  const handleCancelEdit = () => {
    setEditedName(displayName);
    setIsEditingName(false);
  };
  // Avatar upload handling
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = event => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_SIZE = 200;
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        updatePref('avatarUrl', dataUrl);
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
    // Reset input so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleRemoveAvatar = () => {
    updatePref('avatarUrl', undefined);
  };
  // Get linked parents and teachers for the current learner
  const linkedInfo = useMemo(() => {
    if (!accounts || accountRole !== 'learner') return null;
    const learnerId = accounts.activeAccountId;
    if (!learnerId) return null;
    const learnerAccount = accounts.accountsById[learnerId];
    if (!learnerAccount || !isLearnerAccount(learnerAccount)) return null;
    // Get linked parents
    const parentIds = accounts.relationships.learnerToParents[learnerId] || [];
    const parents = parentIds.map(id => accounts.accountsById[id]).filter(Boolean).map(a => ({
      name: a.displayName,
      role: 'Ouder' as const
    }));
    // Get linked teachers
    const teachers: {
      name: string;
      role: 'Docent';
      className?: string;
    }[] = [];
    Object.entries(accounts.relationships.teacherToLearners).forEach(([teacherId, learnerIds]) => {
      if (learnerIds.includes(learnerId)) {
        const teacher = accounts.accountsById[teacherId];
        if (teacher && isTeacherAccount(teacher)) {
          const teacherClassroom = Object.values(accounts.classrooms ?? {}).find(c => c.teacherId === teacherId && c.learnerIds.includes(learnerId));
          teachers.push({
            name: teacher.displayName,
            role: 'Docent',
            className: teacherClassroom?.name
          });
        }
      }
    });
    return {
      parents,
      teachers,
      linkCode: learnerAccount.linkCode,
      learnerId
    };
  }, [accounts, accountRole]);
  // Copy link code state
  const [codeCopied, setCodeCopied] = useState(false);
  const handleCopyCode = () => {
    if (linkedInfo?.linkCode) {
      navigator.clipboard.writeText(linkedInfo.linkCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };
  // Join Classroom State
  const [joinCode, setJoinCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinSuccess, setJoinSuccess] = useState(false);
  const handleJoinClassroom = async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 6) {
      setJoinError('Een klascode bestaat uit 6 tekens.');
      return;
    }
    setJoinLoading(true);
    setJoinError(null);
    try {
      const result = await joinClassroomByCode(code);
      if (result.success) {
        setJoinSuccess(true);
        setJoinCode('');
      } else {
        setJoinError(result.error || 'Klas niet gevonden of al lid.');
      }
    } catch (e) {
      setJoinError('Er is een fout opgetreden bij het koppelen.');
    } finally {
      setJoinLoading(false);
    }
  };
  const dailyGoalOptions: {
    value: 5 | 10 | 20;
    emoji: string;
    label: string;
  }[] = [{
    value: 5,
    emoji: '🌱',
    label: 'Rustig'
  }, {
    value: 10,
    emoji: '⭐',
    label: 'Standaard'
  }, {
    value: 20,
    emoji: '🚀',
    label: 'Uitdaging'
  }];
  const weeklyGoalOptions: {
    value: 3 | 5 | 7;
    emoji: string;
    label: string;
  }[] = [{
    value: 3,
    emoji: '🥉',
    label: 'Basis'
  }, {
    value: 5,
    emoji: '🥈',
    label: 'Actief'
  }, {
    value: 7,
    emoji: '🥇',
    label: 'Elke dag'
  }];
  const periodOptions: {
    value: StartingPeriod | null;
    emoji: string | React.ReactNode;
    label: string;
    fullLabel: string;
  }[] = [{
    value: null,
    emoji: <Sparkles className="w-6 h-6 mx-auto text-emerald-400" data-id="element-2023" data-mp="q73ye1j1j5e6" />,
    label: 'Auto',
    fullLabel: 'Automatisch'
  }, {
    value: 'B',
    emoji: '🌱',
    label: 'Begin',
    fullLabel: 'Begin schooljaar'
  }, {
    value: 'M',
    emoji: '☀️',
    label: 'Midden',
    fullLabel: 'Midden schooljaar'
  }, {
    value: 'E',
    emoji: '🎓',
    label: 'Eind',
    fullLabel: 'Eind schooljaar'
  }];
  const hasMasteryData = Object.keys(state.familyMastery || {}).length > 0;
  return <PageContainer data-id="element-2024" data-mp="31uft52zsx1g4">
      <motion.div variants={itemVariants} data-id="element-2025" data-mp="31w0dy0wbd60s">
        <PageHero title="Instellingen" subtitle="Pas je profiel en voorkeuren aan" gradientClasses="from-orange-500 via-amber-500 to-yellow-500" icon={Settings} iconBgClass="bg-orange-400" djayEmotion="happy" djayVariant={selectedVariant} floatingEmojis={['✨', '🎨']} data-id="element-2026" data-mp="36rpfuavt2z09" />
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-2027" data-mp="2o1zjhjcea568">
        <WorldZone title="Profiel" icon={User} accentColor="bg-orange-100 text-orange-600" isLast={false} data-id="element-2028" data-mp="2o3u9hc2iwnh6">
          <Card className="p-8 md:p-10" data-id="element-2029" data-mp="3bio92qo0s7u">
            <div className="flex flex-col sm:flex-row items-center gap-8" data-id="element-2030" data-mp="3ah1w2478jat">
              {/* Avatar */}
              <div className="flex flex-col items-center gap-3" data-id="element-2031" data-mp="1zdbz1hz8e69c">
                <div className="relative flex-shrink-0 cursor-pointer group" onClick={() => fileInputRef.current?.click()} data-id="element-2032" data-mp="1ugvaimo4dpvq">
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleAvatarUpload} data-id="element-2033" data-mp="24ej9josrdjjp" />
                  <div className="w-28 h-28 rounded-full flex items-center justify-center text-white text-5xl font-bold shadow-lg shadow-orange-200/50 bg-gradient-to-br from-orange-400 to-amber-500 overflow-hidden transition-transform group-hover:scale-105" data-id="element-2034" data-mp="1bqvtmn2fnh3e">
                    {prefs.avatarUrl ? <img src={prefs.avatarUrl} alt="Avatar" className="w-full h-full object-cover" data-id="element-2035" data-mp="wz4zyv5mmd7s" /> : displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-2 shadow-md border border-slate-100 text-slate-400 group-hover:text-orange-500 group-hover:border-orange-200 transition-colors" data-id="element-2036" data-mp="nnz35rnyzpc6">
                    <Camera className="w-5 h-5" data-id="element-2037" data-mp="11lnpl3xor4jc" />
                  </div>
                  <div className="absolute -top-2 -left-2 bg-white rounded-full p-1 shadow-sm z-10" data-id="element-2038" data-mp="11jkrrxqak742">
                    <div className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-2039" data-mp="16inx7ja6ofck">
                      Lvl {state.stats.level}
                    </div>
                  </div>
                </div>
                {prefs.avatarUrl && <button onClick={handleRemoveAvatar} className="text-xs text-rose-500 hover:text-rose-600 font-medium flex items-center gap-1" data-id="element-2040" data-mp="3ccnklimxmvt5">
                    <Trash2 className="w-3 h-3" data-id="element-2041" data-mp="2yn246eymrwg2" />
                    Verwijder foto
                  </button>}
              </div>

              {/* Info */}
              <div className="flex-1 text-center sm:text-left" data-id="element-2042" data-mp="2onjoiapr6wtt">
                {/* Name with Edit */}
                {isEditingName ? <div className="flex items-center gap-3 mb-3 justify-center sm:justify-start" data-id="element-2043" data-mp="2koasez78qmv3">
                    <Input type="text" value={editedName} onChange={e => setEditedName(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') handleCancelEdit();
                }} className="text-3xl font-bold text-slate-900 border-2 border-orange-300 rounded-lg px-4 py-2 focus:border-orange-500 min-h-[56px] max-w-[200px]" autoFocus maxLength={30} fullWidth={false} data-id="element-2044" data-mp="3hc8mi0viim1c" />
                    <button onClick={handleSaveName} className="p-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors min-h-[48px] min-w-[48px] focus:outline-none focus:ring-2 focus:ring-emerald-300 flex items-center justify-center" title="Opslaan" data-id="element-2045" data-mp="1ms6wkiy1ff3s">
                      <Save className="w-5 h-5" data-id="element-2046" data-mp="25ruk0yg0tf4q" />
                    </button>
                    <button onClick={handleCancelEdit} className="p-3 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300 transition-colors min-h-[48px] min-w-[48px] focus:outline-none focus:ring-2 focus:ring-slate-300 flex items-center justify-center" title="Annuleren" data-id="element-2047" data-mp="25pq6dosbl1r9">
                      <X className="w-5 h-5" data-id="element-2048" data-mp="2ojdhyqv6u2zw" />
                    </button>
                  </div> : <div className="flex items-center gap-4 mb-3 justify-center sm:justify-start" data-id="element-2049" data-mp="2dm52mx2s1a67">
                    <h2 className="text-3xl font-black text-slate-900" data-id="element-2050" data-mp="2hmfgevwt94vm">
                      {displayName}
                    </h2>
                    {onUpdateName && <button onClick={() => setIsEditingName(true)} className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors min-h-[40px] min-w-[40px] focus:outline-none focus:ring-2 focus:ring-orange-300 flex items-center justify-center" title="Naam wijzigen" data-id="element-2051" data-mp="2rl4pq0an5h09">
                        <Edit2 className="w-5 h-5" data-id="element-2052" data-mp="3a9jhipss8z24" />
                      </button>}
                  </div>}

                <div className="flex flex-wrap justify-center sm:justify-start gap-3 mt-3" data-id="element-2053" data-mp="3f7upsw3fj1i8">
                  <Badge variant="info" icon={<School className="w-4 h-4" data-id="element-2055" data-mp="18m6szekyyh9v" />} size="lg" data-id="element-2054" data-mp="3k6o7z1tnvgoq">
                    Groep {selectedGroup}
                  </Badge>
                  <Badge variant="default" icon={<Shield className="w-4 h-4" data-id="element-2057" data-mp="yp2gk6gxe069" />} size="lg" data-id="element-2056" data-mp="ymprl9cr2pjp">
                    {roleLabel}
                  </Badge>
                </div>

                {/* XP Progress */}
                <div className="mt-6 max-w-xs mx-auto sm:mx-0" data-id="element-2058" data-mp="1md3ll5ewaygx">
                  <div className="flex items-center justify-between mb-2" data-id="element-2059" data-mp="1mdlmwggf02vp">
                    <span className="text-sm font-semibold text-slate-600" data-id="element-2060" data-mp="1hksk3y4ib4m9">
                      Level {state.stats.level}
                    </span>
                    <span className="text-sm font-bold text-orange-600" data-id="element-2061" data-mp="2a2edryi6jkjg">
                      {state.stats.xp} XP
                    </span>
                  </div>
                  <ProgressBar progress={getProgressToNextLevel(state.stats.xp).percent} color="orange" size="md" animated data-id="element-2062" data-mp="25ad48kcec7x4" />
                  <p className="text-xs text-slate-500 mt-2 text-center sm:text-left" data-id="element-2063" data-mp="8f1q0pi6x6s9">
                    Nog{' '}
                    {getProgressToNextLevel(state.stats.xp).needed - getProgressToNextLevel(state.stats.xp).current}{' '}
                    XP tot Level {state.stats.level + 1}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </WorldZone>
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-2064" data-mp="36vw5a6u35owc">
        <WorldZone title="Voorkeuren" icon={Sliders} accentColor="bg-amber-100 text-amber-600" isLast={false} data-id="element-2065" data-mp="36x7amb36j4pk">
          <div className="space-y-2" data-id="element-2066" data-mp="154sunhk74abs">
            {/* Sound Toggle */}
            <div className="flex items-center justify-between p-4 rounded-2xl hover:bg-white/50 transition-colors" data-id="element-2067" data-mp="1a343boyr8esf">
              <div className="flex items-center gap-4" data-id="element-2068" data-mp="2ulofle0lcs41">
                <div className="w-11 h-11 rounded-xl bg-sky-50 flex items-center justify-center text-sky-600 shadow-sm border border-sky-100" data-id="element-2069" data-mp="2pljzhp3lhzec">
                  <Volume2 className="w-5 h-5" data-id="element-2070" data-mp="34bho265fsr42" />
                </div>
                <div data-id="element-2071" data-mp="2zj6fkewmrwzw">
                  <div className="font-bold text-slate-900" data-id="element-2072" data-mp="2ziol9ntj3sum">
                    Geluidseffecten
                  </div>
                  <div className="text-sm text-slate-600" data-id="element-2073" data-mp="3d9jnwidfpdpr">
                    Geluidjes bij goede antwoorden en beloningen
                  </div>
                </div>
              </div>
              <Toggle checked={prefs.sound} onChange={v => updatePref('sound', v)} label="Geluidseffecten" variant="student" data-id="element-2074" data-mp="3n7zhcrzxoz2f" />
            </div>

            {/* Sound Mix (App-Feel 2027 W2) */}
            <div className={`px-4 pb-4 transition-opacity ${prefs.sound ? 'opacity-100' : 'opacity-40 pointer-events-none select-none'}`} aria-hidden={!prefs.sound} data-id="element-2075" data-mp="1nwyb9svst39n">
              <div className="rounded-2xl bg-white/60 border border-slate-100 p-5 space-y-5" data-id="element-2076" data-mp="jxjgbze1rmnj">
                <div className="text-sm font-semibold text-slate-500" data-id="element-2077" data-mp="ox5xgjwzxn1m">
                  Geluidsbalans
                </div>
                {SOUND_MIX_SLIDERS.map(slider => {
                const value = prefs[slider.key] as number | undefined ?? 1;
                return <div key={slider.key} data-id="element-2078" data-mp="17bm66sbs6jh">
                      <div className="flex items-center justify-between mb-1.5" data-id="element-2079" data-mp="18cz6gswj33u">
                        <div data-id="element-2080" data-mp="58n21kleddeh">
                          <span className="font-bold text-slate-900 text-sm" data-id="element-2081" data-mp="57bzfo3ouboy">
                            {slider.label}
                          </span>
                          <span className="block text-xs text-slate-500" data-id="element-2082" data-mp="39og1e718vzks">
                            {slider.hint}
                          </span>
                        </div>
                        <span className={`text-sm font-bold tabular-nums ${ACCENT_TEXT[slider.accent]}`} data-id="element-2083" data-mp="365h324ve1hhg">
                          {Math.round(value * 100)}%
                        </span>
                      </div>
                      <input type="range" min={0} max={1} step={0.05} value={value} disabled={!prefs.sound} onChange={e => updatePref(slider.key, Number(e.target.value))} onMouseUp={() => previewSound(slider.preview)} onTouchEnd={() => previewSound(slider.preview)} onKeyUp={() => previewSound(slider.preview)} aria-label={slider.label} className={`w-full h-2 rounded-full cursor-pointer bg-slate-200 ${ACCENT_RANGE[slider.accent]}`} data-id="element-2084" data-mp="2x75bo0mp6fny" />
                    </div>;
              })}
              </div>
            </div>

            {/* Reduced Motion Toggle */}
            <div className="flex items-center justify-between p-4 rounded-2xl hover:bg-white/50 transition-colors" data-id="element-2085" data-mp="1fw0d1o3mf00z">
              <div className="flex items-center gap-4" data-id="element-2086" data-mp="1kvmq9xjg8g9s">
                <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 shadow-sm border border-purple-100" data-id="element-2087" data-mp="28k604hi5d13z">
                  <Sparkles className="w-5 h-5" data-id="element-2088" data-mp="2dhxtzh2cqxat" />
                </div>
                <div data-id="element-2089" data-mp="1yuw5ouztz6cp">
                  <div className="font-bold text-slate-900" data-id="element-2090" data-mp="1ytl3v0py48o8">
                    Animaties verminderen
                  </div>
                  <div className="text-sm text-slate-600" data-id="element-2091" data-mp="23rcvdtmohsrw">
                    Rustiger beeld zonder bewegende effecten
                  </div>
                  <div className="text-xs text-purple-600 font-medium mt-0.5" data-id="element-2092" data-mp="2waa62vit5cy5">
                    Wordt actief na herladen
                  </div>
                </div>
              </div>
              <Toggle checked={prefs.reducedMotion} onChange={v => updatePref('reducedMotion', v)} label="Animaties verminderen" variant="student" data-id="element-2093" data-mp="fvy9cae2m8mu" />
            </div>

            <div className="p-4 rounded-2xl flex items-center justify-between" data-id="element-2094" data-mp="pm01bd9job71">
              <div className="flex items-center gap-4" data-id="element-2095" data-mp="1wykp2qk5tqo">
                <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-sm border border-indigo-100" data-id="element-2096" data-mp="3t26sikarwfyx">
                  <Sparkles className="w-5 h-5" data-id="element-2097" data-mp="axd9ic2qdqyx" />
                </div>
                <div data-id="element-2098" data-mp="6ugvkyo4yj1b">
                  <div className="font-bold text-slate-900" data-id="element-2099" data-mp="6u775fiqojxq">Rondleiding</div>
                  <div className="text-sm text-slate-600" data-id="element-2100" data-mp="1maz9yte1jezr">
                    Bekijk de uitleg over de app opnieuw
                  </div>
                </div>
              </div>
              <Button variant="outline" onClick={() => {
              updatePref('pendingAppTour', true);
              updatePref('tourCompleted', false);
              window.location.reload();
            }} data-id="element-2101" data-mp="3bt4i2jzr3kjv">
                Start
              </Button>
            </div>

            {/* Daily Goal Selector */}
            <div className="p-4 rounded-2xl" data-id="element-2102" data-mp="3qjv75k9yk5ja">
              <div className="flex items-center gap-4 mb-5" data-id="element-2103" data-mp="3uk5eb09hl1zq">
                <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shadow-sm border border-amber-100" data-id="element-2104" data-mp="3d2oac5dx1r3">
                  <Target className="w-5 h-5" data-id="element-2105" data-mp="8dgxvymmwjr8" />
                </div>
                <div data-id="element-2106" data-mp="dbs7q9cxiu9g">
                  <div className="font-bold text-slate-900" data-id="element-2107" data-mp="da7ecn2cphd6">Dagelijks doel</div>
                  <div className="text-sm text-slate-600" data-id="element-2108" data-mp="2a422izk285qo">
                    Hoeveel vragen wil je per dag oefenen?
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-2109" data-mp="w7hclmqkqdz2">
                {dailyGoalOptions.map(opt => <button key={opt.value} onClick={() => updatePref('dailyGoal', opt.value)} className={`
                      relative py-5 px-3 rounded-2xl border-2 font-bold transition-all text-center
                      ${prefs.dailyGoal === opt.value ? 'border-amber-400 bg-amber-50 text-amber-800 shadow-[0_2px_0_0_rgb(251,191,36)]' : 'border-white/60 bg-white/40 text-slate-600 hover:border-amber-200 hover:bg-amber-50/50'}
                    `} aria-pressed={prefs.dailyGoal === opt.value} data-id="element-2110" data-mp="rh0vnsnjoa7y">
                    <div className="text-2xl mb-1" data-id="element-2111" data-mp="1173nhpixji4p">{opt.emoji}</div>
                    <div className="text-xl" data-id="element-2112" data-mp="118y9l75xtgad">{opt.value}</div>
                    <div className="text-xs font-semibold text-slate-500 mt-0.5" data-id="element-2113" data-mp="3qwp6q2w8sxdh">
                      {opt.label}
                    </div>
                  </button>)}
              </div>
            </div>

            {/* Weekly Goal Selector */}
            <div className="p-4 rounded-2xl" data-id="element-2114" data-mp="2f6bwhzc3gk09">
              <div className="flex items-center gap-4 mb-5" data-id="element-2115" data-mp="2f7n3s94r09w4">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 shadow-sm border border-emerald-100" data-id="element-2116" data-mp="2sy071y6p30f4">
                  <Calendar className="w-5 h-5" data-id="element-2117" data-mp="2o4nni2gvhjt8" />
                </div>
                <div data-id="element-2118" data-mp="32ud5tah3k4gq">
                  <div className="font-bold text-slate-900" data-id="element-2119" data-mp="32xr70fe0y7ml">Weekdoel</div>
                  <div className="text-sm text-slate-600" data-id="element-2120" data-mp="2xvs1ozyq50n0">
                    Hoeveel dagen per week wil je oefenen?
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-2121" data-mp="37vsoivvsxyvy">
                {weeklyGoalOptions.map(opt => <button key={opt.value} onClick={() => updatePref('weeklyGoal', opt.value)} className={`
                      relative py-5 px-3 rounded-2xl border-2 font-bold transition-all text-center
                      ${prefs.weeklyGoal === opt.value ? 'border-emerald-400 bg-emerald-50 text-emerald-800 shadow-[0_2px_0_0_rgb(52,211,153)]' : 'border-white/60 bg-white/40 text-slate-600 hover:border-emerald-200 hover:bg-emerald-50/50'}
                    `} aria-pressed={prefs.weeklyGoal === opt.value} data-id="element-2122" data-mp="1hjd1ru4ypm50">
                    <div className="text-2xl mb-1" data-id="element-2123" data-mp="jn92f3wjvw8y">{opt.emoji}</div>
                    <div className="text-xl" data-id="element-2124" data-mp="oo5e43n58mox">{opt.value}</div>
                    <div className="text-xs font-semibold text-slate-500 mt-0.5" data-id="element-2125" data-mp="oliz5ixxb4wo">
                      {opt.label}
                    </div>
                  </button>)}
              </div>
            </div>
          </div>
        </WorldZone>
      </motion.div>

      {isLearner && <motion.div variants={itemVariants} data-id="element-2126" data-mp="250amvik4jnzn">
          <WorldZone title="Instapmoment" icon={Calendar} accentColor="bg-teal-100 text-teal-600" isLast={false} data-id="element-2127" data-mp="2a06vxw7oi5m2">
            <div className="space-y-4" data-id="element-2128" data-mp="23kgfwfkulldw">
              <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2129" data-mp="23jesdbmnb6fa">
                <div className="flex items-start gap-4 mb-5" data-id="element-2130" data-mp="1ymoemuybcmx1">
                  <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600 shadow-sm border border-teal-100 flex-shrink-0" data-id="element-2131" data-mp="1tn226nqb7apa">
                    <Calendar className="w-5 h-5" data-id="element-2132" data-mp="2rcn5mvw2iu4c" />
                  </div>
                  <div data-id="element-2133" data-mp="2mcsin3feyook">
                    <div className="font-bold text-slate-900" data-id="element-2134" data-mp="2mdkbi6hmm8b5">
                      Kies je startmoment
                    </div>
                    <div className="text-sm text-slate-600" data-id="element-2135" data-mp="2clxq5hbkqsuo">
                      Bepaalt welke stof als eerste wordt aangeboden. Standaard
                      past dit zich automatisch aan het schooljaar aan.
                    </div>
                  </div>
                </div>

                {hasMasteryData && <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-id="element-2136" data-mp="3cmjg2nxt4usn">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-2137" data-mp="3hlcldt2xxfhq" />
                    <div className="text-sm text-amber-800" data-id="element-2138" data-mp="3miutxid1psp7">
                      Je hebt al geoefend. Om je startmoment opnieuw in te
                      stellen, moet je eerst je voortgang wissen.
                      <button onClick={() => setResetConfirmOpen(true)} className="block mt-2 text-amber-600 font-bold hover:text-amber-700 underline" data-id="element-2139" data-mp="3vjko6hmnrb0t">
                        Voortgang wissen
                      </button>
                    </div>
                  </div>}

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-2140" data-mp="j37a4y3uctx3">
                  {periodOptions.map(opt => <button key={opt.value || 'auto'} onClick={() => onSetStartingPeriod(opt.value)} disabled={hasMasteryData} className={`
                        relative py-4 px-2 rounded-2xl border-2 font-bold transition-all text-center flex flex-col items-center justify-center
                        ${(manualPeriod ?? null) === opt.value ? 'border-teal-400 bg-teal-50 text-teal-800 shadow-[0_2px_0_0_rgb(45,212,191)]' : 'border-slate-200 bg-white text-slate-600 hover:border-teal-200 hover:bg-teal-50/50'}
                        ${hasMasteryData ? 'opacity-50 cursor-not-allowed' : ''}
                      `} aria-pressed={(manualPeriod ?? null) === opt.value} title={opt.fullLabel} data-id="element-2141" data-mp="n5kebi2xwjrr">
                      <div className="text-2xl mb-1 flex items-center justify-center h-8" data-id="element-2142" data-mp="15yhg32dsubvb">
                        {opt.emoji}
                      </div>
                      <div className="text-sm font-semibold mt-1" data-id="element-2143" data-mp="2ha9x13tedl6q">
                        {opt.label}
                      </div>
                    </button>)}
                </div>

                {(manualPeriod ?? null) === null && <div className="mt-4 text-sm text-center text-slate-500 font-medium" data-id="element-2144" data-mp="2fu4gpztk9dcc">
                    Huidig automatisch moment:{' '}
                    <span className="text-teal-600 font-bold" data-id="element-2145" data-mp="2kuaapmnazbxl">
                      {getAutoPhaseLabel()}
                    </span>
                  </div>}
              </div>
            </div>
          </WorldZone>
        </motion.div>}

      {isLearner && <motion.div variants={itemVariants} data-id="element-2146" data-mp="2yts5cina5jev">
          <WorldZone title="Koppelcode & Privacy" icon={Shield} accentColor="bg-blue-100 text-blue-600" isLast={false} data-id="element-2147" data-mp="2ysqizi0tdahu">
            <div className="space-y-6" data-id="element-2148" data-mp="3rgw5oabfh9kb">
              {/* Klascode invoeren (only if no teacher linked) */}
              {linkedInfo && linkedInfo.teachers.length === 0 && !joinSuccess && <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2149" data-mp="9ww5fokykoe0">
                    <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-lg" data-id="element-2150" data-mp="4xt00312gg5i">
                      <Users className="w-5 h-5 text-blue-500" data-id="element-2151" data-mp="2vuh62zwoxd" />
                      Klascode invoeren
                    </h3>
                    <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-2152" data-mp="3n58s4fbndspu">
                      Heb je een code gekregen van je leraar? Vul hem hier in om
                      je aan te sluiten bij de klas.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3" data-id="element-2153" data-mp="3dgh3ag1vvoj5">
                      <Input type="text" value={joinCode} onChange={e => {
                setJoinCode(e.target.value.toUpperCase());
                setJoinError(null);
              }} placeholder="Bijv. A1B2C3" maxLength={6} className="bg-slate-50 border-slate-200 py-3 font-mono text-lg font-bold text-slate-900 focus:ring-blue-500 uppercase" data-id="element-2154" data-mp="38h4gtwexcjh3" />
                      <Button onClick={handleJoinClassroom} disabled={joinCode.trim().length !== 6 || joinLoading} className="py-3 px-6" data-id="element-2155" data-mp="1nz3hf9nvs4cn">
                        {joinLoading ? 'Koppelen...' : 'Koppelen'}
                      </Button>
                    </div>
                    {joinError && <p className="text-sm text-rose-500 mt-3 font-medium flex items-center gap-1.5" data-id="element-2156" data-mp="156j1stx6zvqs">
                        <AlertCircle className="w-4 h-4" data-id="element-2157" data-mp="1a0djjrt8m0n8" />
                        {joinError}
                      </p>}
                  </div>}

              {/* Success message after joining */}
              {joinSuccess && <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-100 shadow-sm flex items-center gap-4" data-id="element-2158" data-mp="36zrylk7d2fs2">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-2159" data-mp="3231gkrbzxa40">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" data-id="element-2160" data-mp="3fz58uf3b78du" />
                  </div>
                  <div data-id="element-2161" data-mp="3c1r0epngd4mw">
                    <h3 className="font-bold text-emerald-900 text-lg" data-id="element-2162" data-mp="3pzfjbrxym1pb">
                      Gekoppeld!
                    </h3>
                    <p className="text-emerald-700 font-medium" data-id="element-2163" data-mp="3l1493g3r5p6k">
                      Je bent succesvol toegevoegd aan de klas.
                    </p>
                  </div>
                </div>}

              {/* Koppelcode */}
              {linkedInfo?.linkCode && <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2164" data-mp="cjzot1fz75ye">
                  <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-lg" data-id="element-2165" data-mp="7ly8go9xpkqm">
                    <LinkIcon className="w-5 h-5 text-blue-500" data-id="element-2166" data-mp="r6q5ba29t54k" />
                    Mijn Koppelcode
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-2167" data-mp="w59nr0hnnys6">
                    Deel deze code met je ouder(s) zodat zij je voortgang kunnen
                    volgen.
                  </p>
                  <div className="flex items-center gap-3" data-id="element-2168" data-mp="m6txt0ud6vk9">
                    <div className="bg-blue-50 border border-blue-100 px-5 py-2.5 rounded-xl font-mono text-xl font-black tracking-widest text-blue-900" data-id="element-2169" data-mp="8h09cjmrudb2">
                      {linkedInfo.linkCode}
                    </div>
                    <button onClick={handleCopyCode} className="p-2.5 bg-blue-50 border border-blue-100 rounded-xl text-blue-600 hover:bg-blue-100 transition-all" title="Kopieer code" data-id="element-2170" data-mp="3uozvvrqjkfdu">
                      {codeCopied ? <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-2171" data-mp="3ql1xjj91j8lu" /> : <Copy className="w-5 h-5" data-id="element-2172" data-mp="liptm1bdjve2" />}
                    </button>
                    {codeCopied && <span className="text-sm font-bold text-emerald-600" data-id="element-2173" data-mp="bseb99qy1jnn">
                        Gekopieerd!
                      </span>}
                  </div>
                </div>}

              {/* Wie kan mijn voortgang zien? */}
              {linkedInfo && (linkedInfo.parents.length > 0 || linkedInfo.teachers.length > 0) && <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2174" data-mp="3oz6byz9dwdd8">
                    <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-lg" data-id="element-2175" data-mp="3txpxiyk2fn52">
                      <Eye className="w-5 h-5 text-emerald-500" data-id="element-2176" data-mp="1n7takk6mknlx" />
                      Wie kan mijn voortgang zien?
                    </h3>
                    <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-2177" data-mp="1s5bgs2t9coq8">
                      Deze mensen kunnen je scores bekijken, maar niet je
                      wachtwoord of instellingen wijzigen.
                    </p>
                    <div className="space-y-4" data-id="element-2178" data-mp="1x9nfd3y0972r">
                      {linkedInfo.parents.length > 0 && <div data-id="element-2179" data-mp="26c6gnjm57bmw">
                          <div className="flex items-center gap-2 mb-2" data-id="element-2180" data-mp="26bwobpdmkse1">
                            <Heart className="w-4 h-4 text-rose-500" data-id="element-2181" data-mp="2l12phiw0wsok" />
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-2182" data-mp="2l35mimvlfm2s">
                              Ouders / Verzorgers
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2" data-id="element-2183" data-mp="2q05skxsg0otw">
                            {linkedInfo.parents.map((p, i) => <div key={i} className="flex items-center gap-2 bg-rose-50 rounded-lg px-3 py-1.5 border border-rose-100" data-id="element-2184" data-mp="zmy92hw9s8b3" data-mpi={i}>
                                <span className="text-sm font-semibold text-rose-900" data-id="element-2185" data-mp="2usfslu9v4rht">
                                  {p.name}
                                </span>
                              </div>)}
                          </div>
                        </div>}
                      {linkedInfo.teachers.length > 0 && <div data-id="element-2186" data-mp="3dop8mn4brs4g">
                          <div className="flex items-center gap-2 mb-2" data-id="element-2187" data-mp="3dnnzwpas7hpw">
                            <GraduationCap className="w-4 h-4 text-orange-500" data-id="element-2188" data-mp="3inipmpitm19d" />
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-2189" data-mp="3nfa7mbnwuynw">
                              Docenten
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2" data-id="element-2190" data-mp="19d9xk9k4jo9">
                            {linkedInfo.teachers.map((t, i) => <div key={i} className="flex items-center gap-2 bg-orange-50 rounded-lg px-3 py-1.5 border border-orange-100" data-id="element-2191" data-mp="57uim88mm27s" data-mpi={i}>
                                <span className="text-sm font-semibold text-orange-900" data-id="element-2192" data-mp="1gr1l13iffoy5">
                                  {t.name}
                                </span>
                                {t.className && <span className="text-xs text-orange-600" data-id="element-2193" data-mp="29exabn5o2ekw">
                                    ({t.className})
                                  </span>}
                              </div>)}
                          </div>
                        </div>}
                    </div>
                  </div>}
            </div>
          </WorldZone>
        </motion.div>}

      <motion.div variants={itemVariants} data-id="element-2194" data-mp="2nf81ysesvx74">
        <WorldZone title="Waar staan mijn gegevens?" icon={Shield} accentColor="bg-indigo-100 text-indigo-600" isLast={false} data-id="element-2195" data-mp="2ne5b2khh9ur7">
          <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm space-y-4" data-id="element-2196" data-mp="2c0d48ou9mqqo">
            {session.status === 'authenticated' ? <>
                <div className="flex items-start gap-4" data-id="element-2197" data-mp="22ydsaay3ycul">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-sm border flex-shrink-0 ${shouldUseBackend() ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-50 text-amber-600 border-amber-100'}`} data-id="element-2198" data-mp="22yvqve88t35o">
                    <Shield className="w-5 h-5" data-id="element-2199" data-mp="2lw6pcwm1vsk2" />
                  </div>
                  <div data-id="element-2200" data-mp="39msnnw695ygv">
                    <div className="font-bold text-slate-900" data-id="element-2201" data-mp="39mamcl4qgu23">
                      {shouldUseBackend() ? 'Voortgang op de server' : 'Voortgang alleen op dit apparaat'}
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5" data-id="element-2202" data-mp="xzqkunu3kjn9">
                      {shouldUseBackend() ? 'Je voortgang wordt bewaard en is op elk apparaat beschikbaar.' : 'Je inloggegevens staan wél op de server, maar je voortgang blijft op dit apparaat. Zonder toestemming van een ouder of school bewaren wij die niet.'}
                    </p>
                  </div>
                </div>

                {/* De muur, in gewone taal. Het onderscheid operationeel vs.
                    evaluatief is voor een gebruiker betekenisloos; wat telt is
                    wat er met zijn spullen gebeurt. */}
                {session.profile.role === 'learner' && <ul className="pt-4 border-t border-slate-100 space-y-2 text-sm" data-id="element-2203" data-mp="24f9megrocsef">
                    <li className="flex items-start gap-2.5" data-id="element-2204" data-mp="1zgpwk59ymwgs">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-2205" data-mp="1zgg8woce11e9" />
                      <span className="text-slate-700" data-id="element-2206" data-mp="x2s7ggv4otxp">
                        Oefenen, je niveau en je streak — dit werkt altijd en
                        gaat mee naar een ander apparaat.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5" data-id="element-2207" data-mp="1b2t39hwjdb6b">
                      {session.profile.consentStatus === 'granted' || session.profile.dataController === 'school' ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-2208" data-mp="idvy8ghc5avi" /> : <Shield className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-2209" data-mp="ncfj0dk70gma" />}
                      <span className="text-slate-700" data-id="element-2210" data-mp="s5ry9x2zfb2d">
                        Toetsuitslagen bewaren
                        {session.profile.consentStatus === 'granted' || session.profile.dataController === 'school' ? ' — dit wordt bewaard.' : ' — hiervoor moet eerst een ouder of school aan je account gekoppeld zijn. Toetsen maken kan gewoon; de uitslag blijft alleen op dit apparaat.'}
                      </span>
                    </li>
                  </ul>}

                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 pt-4 border-t border-slate-100 text-sm" data-id="element-2211" data-mp="3aajcspdnh4n6">
                  <dt className="text-slate-500 font-medium" data-id="element-2212" data-mp="35b6py4ms3xkl">Ingelogd als</dt>
                  <dd className="text-slate-900 font-semibold" data-id="element-2213" data-mp="30kgkysi9lwqf">
                    {session.profile.displayName ?? '—'}
                  </dd>

                  <dt className="text-slate-500 font-medium" data-id="element-2214" data-mp="2vlvkcrz3pb1g">Rol</dt>
                  <dd className="text-slate-900 font-semibold" data-id="element-2215" data-mp="2qmivk1oo5tw8">
                    {getAccountRoleLabel(session.profile.role)}
                  </dd>

                  <dt className="text-slate-500 font-medium" data-id="element-2216" data-mp="2mjmdcc31jzst">
                    Wie is verantwoordelijk
                  </dt>
                  <dd className="text-slate-900 font-semibold" data-id="element-2217" data-mp="9cmmwut6ns0l">
                    {session.profile.dataController === 'school' ? 'De school' : 'Jij / je ouders'}
                  </dd>

                  <dt className="text-slate-500 font-medium" data-id="element-2218" data-mp="awnb1p5dmrq">Toestemming</dt>
                  <dd className="text-slate-900 font-semibold" data-id="element-2219" data-mp="4d0cet4qopvh">
                    {{
                  granted: 'Gegeven',
                  pending: 'Aangevraagd',
                  withdrawn: 'Ingetrokken',
                  local: 'Niet nodig (lokaal)'
                }[session.profile.consentStatus]}
                  </dd>
                </dl>

                {/* Wie kijkt er mee — van de server, met de toestemmingsstatus
                    erbij. Zonder dit is "gekoppeld maar nog geen toestemming"
                    een onzichtbare tussentoestand, en dat is precies de fase
                    waarin een gebruiker zich afvraagt of het gelukt is. */}
                {session.profile.role === 'learner' && <div className="pt-4 border-t border-slate-100" data-id="element-2220" data-mp="2k7emkys76a4l">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2" data-id="element-2221" data-mp="2k97usiigl2ep">
                      Wie kan mijn voortgang zien?
                    </h4>
                    {serverParents === null ? <p className="text-sm text-slate-400" data-id="element-2222" data-mp="384kccltozr4k">Laden…</p> : serverParents.length === 0 ? <p className="text-sm text-slate-600" data-id="element-2223" data-mp="334plgi9x31ji">
                        Nog niemand. Geef je koppelcode aan je ouder of je
                        leerkracht.
                      </p> : <ul className="space-y-2" data-id="element-2224" data-mp="3cw2cj7nw5noh">
                        {serverParents.map(p => <li key={p.id} className="flex items-center justify-between gap-3 bg-slate-50 rounded-lg px-3 py-2" data-id="element-2225" data-mp="3qvl8z37cbaky">
                            <span className="text-sm font-semibold text-slate-800" data-id="element-2226" data-mp="s0y6joi4b4jl">
                              {p.name}
                            </span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${session.profile.consentStatus === 'granted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-2227" data-mp="i3tjlmgg751q">
                              {session.profile.consentStatus === 'granted' ? 'Toestemming gegeven' : 'Gekoppeld — nog geen toestemming'}
                            </span>
                          </li>)}
                      </ul>}
                  </div>}

                <p className="text-xs text-slate-400 font-mono pt-2 border-t border-slate-100 break-all" data-id="element-2228" data-mp="1x2gy9x51znww">
                  {session.profile.id}
                </p>
              </> : <div className="flex items-start gap-4" data-id="element-2229" data-mp="26242srt2x9u6">
                <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shadow-sm border border-amber-100 flex-shrink-0" data-id="element-2230" data-mp="263f84w26apne">
                  <AlertCircle className="w-5 h-5" data-id="element-2231" data-mp="1dezqs2vkjv8w" />
                </div>
                <div data-id="element-2232" data-mp="1icrstppxgxqt">
                  <div className="font-bold text-slate-900" data-id="element-2233" data-mp="1ie2zbxarcjlm">
                    {session.status === 'loading' ? 'Bezig met controleren…' : session.status === 'incomplete' ? 'Account niet volledig aangemaakt' : 'Alleen op dit apparaat'}
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5" data-id="element-2234" data-mp="ulmdrcrta9hk">
                    {session.status === 'incomplete' ? 'Je bent ingelogd, maar er is geen profiel gevonden. Neem contact op met de beheerder.' : 'Er is geen serveraccount actief. Alles wat je doet blijft op dit apparaat.'}
                  </p>
                </div>
              </div>}
          </div>
        </WorldZone>
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-2235" data-mp="wdvlatyado9i">
        <WorldZone title="Account" icon={LogOut} accentColor="bg-slate-100 text-slate-600" isLast={true} data-id="element-2236" data-mp="8mzzqekfbpap">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4" data-id="element-2237" data-mp="4mpqmt1c4irm">
            <div data-id="element-2238" data-mp="ilywayrwzuhc">
              <h3 className="text-lg font-black text-slate-800" data-id="element-2239" data-mp="imh4mtrupz5m">
                Sessie beëindigen
              </h3>
              <p className="text-slate-600 font-medium" data-id="element-2240" data-mp="dofl67qijxtm">
                Je spullen blijven op dit apparaat staan, zodat je zo verder
                kunt.
              </p>
            </div>
            <Button onClick={onLogout} variant="secondary" className="w-full sm:w-auto text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-900" data-id="element-2241" data-mp="1p3ca7hmp6n0z">
              <LogOut className="w-5 h-5 mr-2" data-id="element-2242" data-mp="2xy6g9vb2nlft" />
              Uitloggen
            </Button>
          </div>

          {/* Profielrooster. Standaard UIT: een rij namen op een klassikaal
              apparaat verraadt wie er in die klas zit, en dat is zelf een
              verwerking. De gebruiker zet het per apparaat aan. */}
          <div className="flex items-center justify-between gap-4 p-4 border-t border-slate-100" data-id="element-2243" data-mp="3uop757ksbacy">
            <div data-id="element-2244" data-mp="3gxu3qat21lgr">
              <h3 className="text-base font-black text-slate-800" data-id="element-2245" data-mp="3gw9bwsy4kgml">
                Onthoud wie dit apparaat gebruikt
              </h3>
              <p className="text-sm text-slate-600" data-id="element-2246" data-mp="3ltsntm7cohbd">
                Dan staat je naam klaar op het inlogscherm. Handig als je met
                broer of zus dezelfde tablet deelt. Je wachtwoord blijft altijd
                nodig.
              </p>
            </div>
            <Toggle checked={rosterOn} onChange={v => {
            setRosterEnabled(v);
            setRosterOn(v);
          }} data-id="element-2247" data-mp="2ps6nsvytet3o" />
          </div>

          {/* Tweede, zwaardere optie. Bewust apart en visueel rustiger dan
              "Uitloggen": dit is wat je kiest op een geleend of gedeeld
              apparaat, niet wat je dagelijks doet. */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-slate-100" data-id="element-2248" data-mp="38glfzmkvcd62">
            <div data-id="element-2249" data-mp="34hm9hxjho8ci">
              <h3 className="text-base font-black text-slate-800" data-id="element-2250" data-mp="2zot4r9o0t00f">
                Afmelden op dit apparaat
              </h3>
              <p className="text-sm text-slate-600" data-id="element-2251" data-mp="2uqhx95l771ld">
                Uitloggen én al je spullen van dit apparaat verwijderen. Handig
                op een geleende tablet of een computer op school.
              </p>
            </div>
            <button type="button" onClick={() => setWipeConfirmOpen(true)} disabled={wiping} className="w-full sm:w-auto flex-shrink-0 px-4 py-2 rounded-xl text-sm font-bold text-rose-600 border border-rose-200 hover:bg-rose-50 disabled:opacity-50 transition-colors" data-id="element-2252" data-mp="3nd2o84xrhboe">
              Afmelden en wissen
            </button>
          </div>
        </WorldZone>
      </motion.div>

      <ConfirmDialog open={resetConfirmOpen} title="Voortgang wissen" message="Weet je zeker dat je alle voortgang wilt wissen? Dit kan niet ongedaan worden gemaakt." confirmLabel="Voortgang wissen" cancelLabel="Annuleren" variant="danger" onConfirm={confirmResetProgress} onCancel={() => setResetConfirmOpen(false)} data-id="element-2253" data-mp="hj9v17no3apv" />

      {/* De waarschuwing moet KLOPPEN, niet alleen streng klinken. Wat er
          werkelijk verdwijnt hangt af van of dit account meesynchroniseert:
          oefenvoortgang staat dan ook op de server en komt terug bij de
          volgende keer inloggen. Toetsuitslagen staan altijd alleen hier —
          die zijn na dit moment echt weg. */}
      <ConfirmDialog open={wipeConfirmOpen} title="Afmelden op dit apparaat" message={!hasServerAccount ? 'Dit account staat alleen op dit apparaat. Als je afmeldt, is ALLES weg: je voortgang, je sterren en je toetsuitslagen. Je kunt het daarna niet meer terughalen.' : syncsToServer ? 'Je oefenvoortgang staat ook op de server en komt terug zodra je weer inlogt. Je toetsuitslagen staan alleen op dit apparaat en zijn daarna echt weg.' : 'Je oefenvoortgang staat ook op de server en komt terug zodra je weer inlogt. Je toetsuitslagen staan alleen op dit apparaat — dit account is niet gekoppeld aan een ouder — en zijn daarna echt weg.'} confirmLabel="Afmelden en wissen" cancelLabel="Annuleren" variant="danger" onConfirm={() => void confirmWipeDevice()} onCancel={() => setWipeConfirmOpen(false)} data-id="element-2254" data-mp="2xbf9g5y3fqom" />
    </PageContainer>;
}